import React, { useState } from 'react';
import { Plus, FileText, Search, Loader2, Copy, Trash2, Play, Clock, CheckCircle2, AlertCircle, Download } from 'lucide-react';

const STATUS_CONFIG = {
  draft: { icon: Clock, color: 'text-gray-500', bg: 'bg-gray-100 dark:bg-gray-700', label: 'Draft' },
  parsing: { icon: Loader2, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30', label: 'Parsing' },
  generating: { icon: Loader2, color: 'text-orange-500', bg: 'bg-orange-100 dark:bg-orange-900/30', label: 'Generating' },
  completed: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-100 dark:bg-green-900/30', label: 'Completed' },
  error: { icon: AlertCircle, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30', label: 'Error' },
};

const DocumentHome = ({
  isDark, documents, loadingDocs,
  onNewDocument, onLoadDocument, onDuplicateDocument, onDeleteDocument
}) => {
  const [search, setSearch] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const filtered = documents.filter(d =>
    !search || (d.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (d.topic || '').toLowerCase().includes(search.toLowerCase())
  );

  const recentDocs = filtered.slice(0, 20);

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Top bar */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search documents..."
              className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm ${
                isDark
                  ? 'bg-gray-800 border-gray-700 text-gray-100 placeholder-gray-500'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
          </div>
        </div>
        <button
          onClick={onNewDocument}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors shadow-sm"
        >
          <Plus size={18} />
          New Document
        </button>
      </div>

      {/* Document List */}
      {loadingDocs ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 size={24} className="animate-spin text-blue-500" />
        </div>
      ) : recentDocs.length === 0 ? (
        <div className={`text-center py-16 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          <FileText size={56} className="mx-auto mb-4 opacity-40" />
          <p className="text-lg font-medium mb-1">
            {search ? 'No documents match your search' : 'No training documents yet'}
          </p>
          <p className="text-sm mb-4">
            {search ? 'Try a different search term' : 'Create your first AI-powered training document'}
          </p>
          {!search && (
            <button
              onClick={onNewDocument}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600 transition-colors"
            >
              <Plus size={16} />
              Create Document
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {recentDocs.map(doc => {
            const config = STATUS_CONFIG[doc.status] || STATUS_CONFIG.draft;
            const StatusIcon = config.icon;
            const sectionCount = doc.generated_sections?.length || 0;

            return (
              <div
                key={doc.id}
                className={`group flex items-center gap-4 p-4 rounded-xl border transition-colors cursor-pointer ${
                  isDark
                    ? 'border-gray-700 bg-gray-800 hover:bg-gray-750 hover:border-gray-600'
                    : 'border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300'
                }`}
                onClick={() => onLoadDocument(doc)}
              >
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <FileText size={20} className="text-blue-500" />
                </div>

                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{doc.title}</div>
                  <div className={`flex items-center gap-3 text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {doc.topic && doc.topic !== doc.title && <span>{doc.topic}</span>}
                    <span>{new Date(doc.updated_at || doc.created_at).toLocaleDateString()}</span>
                    {sectionCount > 0 && <span>{sectionCount} sections</span>}
                  </div>
                </div>

                {/* Status */}
                <span className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full ${config.bg} ${config.color}`}>
                  <StatusIcon size={12} className={doc.status === 'generating' ? 'animate-spin' : ''} />
                  {config.label}
                </span>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                  {doc.generated_sections?.length > 0 && (
                    <button
                      onClick={() => onDuplicateDocument(doc)}
                      title="Duplicate with new topic"
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400 hover:text-blue-400' : 'hover:bg-gray-100 text-gray-400 hover:text-blue-500'}`}
                    >
                      <Copy size={15} />
                    </button>
                  )}
                  {confirmDelete === doc.id ? (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => { onDeleteDocument(doc.id); setConfirmDelete(null); }}
                        className="px-2 py-1 text-xs rounded bg-red-500 text-white hover:bg-red-600"
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className={`px-2 py-1 text-xs rounded ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(doc.id)}
                      title="Delete"
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700 text-gray-400 hover:text-red-400' : 'hover:bg-gray-100 text-gray-400 hover:text-red-500'}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DocumentHome;
